import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { useJsonData } from '../hooks/useJsonData';
import { useSafeAnimation } from '../components/ui/SafeMotion';

const SITE_URL = 'https://www.patekarecycle.com';

interface ServiceData {
  title: string;
  description: string;
  features: string[];
  process: string[];
  benefits: string[];
}

type ServicesDetailData = Record<string, ServiceData>;

const servicesList = [
  { id: 'integrated-waste-management', name: 'Integrated Waste Management' },
  { id: 'resource-recovery', name: 'Resource Recovery' },
  { id: 'compliance-epr', name: 'Compliance & EPR Services' },
  { id: 'recycling-solutions', name: 'Recycling Solutions' },
  { id: 'community-initiatives', name: 'Community Initiatives' },
  { id: 'esg-reporting', name: 'ESG Reporting' },
];

export function ServiceDetail() {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { data: servicesData, loading } = useJsonData<ServicesDetailData>(
    '/data/services-detail.json'
  );
  const leftAnim = useSafeAnimation({ opacity: 0, x: -30 }, { opacity: 1, x: 0 });

  const service = serviceId && servicesData ? servicesData[serviceId] : null;

  if (loading) {
    return (
      <Layout>
        <div className="pt-32 pb-16 bg-gray-950 min-h-screen">
          <div className="container mx-auto px-4">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-800 rounded w-24" />
              <div className="h-10 bg-gray-800 rounded w-3/4" />
              <div className="h-24 bg-gray-800 rounded" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!service) {
    return (
      <Layout>
        <div className="pt-32 pb-16 bg-gray-950 min-h-screen text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Service not found</h1>
          <Link to="/services" className="text-p-400 hover:underline">
            View all services
          </Link>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Helmet>
        <title>{service.title} | Punarvaa - Waste Management Services India</title>
        <meta name="title" content={`${service.title} | Punarvaa - Waste Management Services India`} />
        <meta name="description" content={service.description} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${SITE_URL}/services/${serviceId}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/services/${serviceId}`} />
        <meta property="og:title" content={`${service.title} | Punarvaa`} />
        <meta property="og:description" content={service.description} />
        <meta property="og:site_name" content="Punarvaa by Patekar Recycle" />
        <meta property="og:image" content={`${SITE_URL}/images/og-image.png`} />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@patekarrecycle" />
        <meta name="twitter:title" content={`${service.title} | Punarvaa`} />
        <meta name="twitter:description" content={service.description} />
        <meta name="twitter:image" content={`${SITE_URL}/images/og-image.png`} />
      </Helmet>

      {/* Hero — dark editorial */}
      <section className="pt-32 pb-16 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-p-500 hover:text-p-400 text-sm font-medium mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            All Services
          </Link>
          <motion.div
            initial={leftAnim.initial}
            animate={leftAnim.animate}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-display text-white mb-5 leading-tight">
              {service.title}
            </h1>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed">{service.description}</p>
            <Link
              to="/contact?quote=true"
              className="inline-flex items-center gap-2 px-6 py-3 bg-p-600 text-white font-semibold rounded-xl hover:bg-p-700 transition-colors"
            >
              Get Quote
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features — 2-col editorial list with teal left-border */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold font-display text-gray-900 mb-8">What We Offer</h2>
          <div className="grid sm:grid-cols-2 gap-0">
            {service.features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className={`pl-5 border-l-2 border-p-600 py-4 ${
                  index < service.features.length - 1 ? 'border-b border-b-gray-100' : ''
                } ${index % 2 === 0 && index < service.features.length - 1 ? 'mr-8' : ''}`}
              >
                <p className="text-sm font-semibold text-gray-900 leading-snug">{feature}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process — dark bg horizontal timeline */}
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-white mb-10">Our Process</h2>
          <div className="flex flex-wrap items-center gap-2">
            {service.process.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -8 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className="flex items-center gap-2"
              >
                <div className="flex items-center gap-3 px-5 py-3 backdrop-blur-sm bg-white/5 border border-gray-700 rounded-full">
                  <span className="text-xs font-bold text-p-500 tabular-nums">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="text-sm font-medium text-gray-200">{step}</span>
                </div>
                {index < service.process.length - 1 && (
                  <ArrowRight className="w-4 h-4 text-gray-700 shrink-0" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits — white bg, 2-col with teal checkmarks */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold font-display text-gray-900 mb-8">Benefits</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {service.benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className="flex items-start gap-3"
              >
                <CheckCircle className="w-5 h-5 text-p-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 font-medium">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Other Services — clean white with gray border pills */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-5">
            Other Services
          </p>
          <div className="flex flex-wrap gap-3">
            {servicesList
              .filter((s) => s.id !== serviceId)
              .map((s) => (
                <Link
                  key={s.id}
                  to={`/services/${s.id}`}
                  className="px-4 py-2 border border-gray-200 text-gray-600 rounded-full hover:border-p-600 hover:text-p-600 transition-colors text-sm font-medium"
                >
                  {s.name}
                </Link>
              ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-p-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2">
              Need a Reliable Waste Recovery Partner?
            </h2>
            <p className="text-p-100">Get a customized waste recovery plan for your facility.</p>
          </div>
          <Link
            to="/contact?quote=true"
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-p-800 font-semibold rounded-xl hover:bg-p-50 transition-colors shrink-0"
          >
            Request Quote
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
